import { useState, useEffect, useRef, Fragment, type ComponentType, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import SearchableSelect, { type SearchableSelectOption } from '../components/SearchableSelect';
import {
  BookOpen,
  TrendingUp,
  Scale,
  ListTree,
  Table2,
  Plus,
  Download,
  Calendar,
  Search,
  Edit,
  Trash2,
  X,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Users,
  Building2,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import {
  getAllAccounts,
  createAccount,
  updateAccount,
  deleteAccount,
} from '../utils/accountApi';
import {
  getTrialBalance,
  getProfitAndLoss,
  getProfitAndLossDetail,
  getBalanceSheet,
  getArAging,
  getApAging,
} from '../utils/reportApi';
import { getCustomers, getCustomerById } from '../utils/customerApi';
import { getSuppliers, getSupplierById } from '../utils/supplierApi';
import { getJournalEntries } from '../utils/journalEntryApi';
import { todayLocalIso } from '../utils/localDate';
import { ApiValidationError } from '../types/auth';
import { toast, toastApiError } from '../utils/toast';
import type {
  GlAccount,
  CreateAccountData,
  UpdateAccountData,
  AccountType,
  AccountSubType,
} from '../types/account';
import type {
  TrialBalanceReport,
  ProfitAndLossReport,
  BalanceSheetReport,
  BalanceSheetAccount,
  ArAgingReport,
  ApAgingReport,
  AgingBucketTotals,
} from '../types/report';
import type { Customer } from '../types/customer';
import type { Supplier } from '../types/supplier';
import type { JournalEntry, PaginationMeta } from '../types/journalEntry';
import { formatCurrency } from '../utils/formatCurrency';
import ProfitAndLossAccountDetail from '../components/profit-and-loss/ProfitAndLossAccountDetail';
import { getReportSourceLabel } from '../utils/reportSourceNavigation';

type AccountTab =
  | 'journal'
  | 'income-statement'
  | 'balance-sheet'
  | 'trial-balance'
  | 'ar-aging'
  | 'ap-aging'
  | 'chart-of-accounts';

const tabs: { id: AccountTab; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { id: 'journal', label: 'Journal', icon: BookOpen },
  { id: 'income-statement', label: 'Income Statement', icon: TrendingUp },
  { id: 'balance-sheet', label: 'Balance Sheet', icon: Scale },
  { id: 'trial-balance', label: 'Trial Balance', icon: Table2 },
  { id: 'ar-aging', label: 'AR Aging', icon: Users },
  { id: 'ap-aging', label: 'AP Aging', icon: Building2 },
  { id: 'chart-of-accounts', label: 'Chart of Accounts', icon: ListTree },
];

const AGING_BUCKET_COLUMNS: { key: keyof AgingBucketTotals; label: string }[] = [
  { key: 'current', label: 'Current' },
  { key: 'days_1_30', label: '1–30 Days' },
  { key: 'days_31_60', label: '31–60 Days' },
  { key: 'days_61_90', label: '61–90 Days' },
  { key: 'days_over_90', label: 'Over 90 Days' },
  { key: 'total', label: 'Total' },
];

const AGING_BUCKET_LABELS: Record<string, string> = {
  current: 'Current',
  days_1_30: '1–30 Days',
  days_31_60: '31–60 Days',
  days_61_90: '61–90 Days',
  days_over_90: 'Over 90 Days',
};

const formatAgingAmount = (amount: string) =>
  parseFloat(amount) > 0 ? formatCurrency(amount) : '—';

const customerToOption = (customer: Customer): SearchableSelectOption => ({
  value: customer.id,
  label: customer.display_name,
  description: [customer.email, customer.phone].filter(Boolean).join(' · ') || undefined,
});

const supplierToOption = (supplier: Supplier): SearchableSelectOption => ({
  value: supplier.id,
  label: supplier.display_name,
  description: [supplier.email, supplier.phone].filter(Boolean).join(' · ') || undefined,
});

const SOURCE_TYPE_LABELS: Record<string, string> = {
  INVOICE: 'Invoice',
  BILL: 'Bill',
  PAYMENT: 'Payment',
  BILL_PAYMENT: 'Bill Payment',
  JOURNAL_ENTRY: 'Journal Entry',
};

const ACCOUNT_TYPES: AccountType[] = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];

const ACCOUNT_SUB_TYPES: AccountSubType[] = [
  'CASH',
  'BANK',
  'ACCOUNTS_RECEIVABLE',
  'INVENTORY_ASSET',
  'FIXED_ASSET',
  'ACCOUNTS_PAYABLE',
  'VAT_PAYABLE',
  'INPUT_VAT',
  'SALES_REVENUE',
  'COGS',
  'OPERATING_EXPENSE',
  'WRITE_OFF',
  'OTHER',
];

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const todayIso = todayLocalIso;

const firstDayOfMonth = (dateIso: string) => {
  const [year, month] = dateIso.split('-');
  return `${year}-${month}-01`;
};

const canAccessAccounts = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const canDeleteAccount = () => localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const defaultFormData = (): CreateAccountData => ({
  account_number: '',
  name: '',
  account_type: 'ASSET',
  account_sub_type: 'OTHER',
  parent_account_id: null,
  active: true,
});

interface FieldErrors {
  [key: string]: string[] | undefined;
}

interface SelectedPlAccount {
  account_id: number;
  account_number: string;
  name: string;
}

type PlView = 'summary' | 'detail';

const Accounts = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<AccountTab>('income-statement');
  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<GlAccount | null>(null);
  const [formData, setFormData] = useState<CreateAccountData>(defaultFormData());
  const [modalError, setModalError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [trialBalance, setTrialBalance] = useState<TrialBalanceReport | null>(null);
  const [trialBalanceLoading, setTrialBalanceLoading] = useState(false);
  const [trialBalanceError, setTrialBalanceError] = useState<string | null>(null);
  const [asOfDate, setAsOfDate] = useState(todayIso);
  const [startDate, setStartDate] = useState(() => firstDayOfMonth(todayIso()));
  const [endDate, setEndDate] = useState(todayIso);
  const [includeZeroBalances, setIncludeZeroBalances] = useState(false);
  const [profitAndLoss, setProfitAndLoss] = useState<ProfitAndLossReport | null>(null);
  const [profitAndLossLoading, setProfitAndLossLoading] = useState(false);
  const [profitAndLossError, setProfitAndLossError] = useState<string | null>(null);
  const [plView, setPlView] = useState<PlView>('summary');
  const [selectedPlAccount, setSelectedPlAccount] = useState<SelectedPlAccount | null>(null);
  const pendingPlAccountIdRef = useRef<number | null>(null);
  const [balanceSheet, setBalanceSheet] = useState<BalanceSheetReport | null>(null);
  const [balanceSheetLoading, setBalanceSheetLoading] = useState(false);
  const [balanceSheetError, setBalanceSheetError] = useState<string | null>(null);
  const [arAging, setArAging] = useState<ArAgingReport | null>(null);
  const [arAgingLoading, setArAgingLoading] = useState(false);
  const [arAgingError, setArAgingError] = useState<string | null>(null);
  const [arCustomerFilter, setArCustomerFilter] = useState('');
  const [expandedArCustomers, setExpandedArCustomers] = useState<Set<number>>(new Set());
  const [customerOptions, setCustomerOptions] = useState<SearchableSelectOption[]>([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedArCustomerOption, setSelectedArCustomerOption] =
    useState<SearchableSelectOption | null>(null);
  const selectedArCustomerOptionRef = useRef<SearchableSelectOption | null>(null);
  const customerLookupRef = useRef<Map<number, Customer>>(new Map());
  const [apAging, setApAging] = useState<ApAgingReport | null>(null);
  const [apAgingLoading, setApAgingLoading] = useState(false);
  const [apAgingError, setApAgingError] = useState<string | null>(null);
  const [apVendorFilter, setApVendorFilter] = useState('');
  const [expandedApVendors, setExpandedApVendors] = useState<Set<number>>(new Set());
  const [vendorOptions, setVendorOptions] = useState<SearchableSelectOption[]>([]);
  const [vendorSearchInput, setVendorSearchInput] = useState('');
  const [vendorSearchLoading, setVendorSearchLoading] = useState(false);
  const [selectedApVendorOption, setSelectedApVendorOption] =
    useState<SearchableSelectOption | null>(null);
  const selectedApVendorOptionRef = useRef<SearchableSelectOption | null>(null);
  const supplierLookupRef = useRef<Map<number, Supplier>>(new Map());
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [journalMeta, setJournalMeta] = useState<PaginationMeta | null>(null);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalError, setJournalError] = useState<string | null>(null);
  const [journalPage, setJournalPage] = useState(1);
  const [journalAccountFilter, setJournalAccountFilter] = useState('');
  const [journalAutoFilter, setJournalAutoFilter] = useState('');
  const [journalReferenceFilter, setJournalReferenceFilter] = useState<number | null>(null);
  const [expandedJournalEntries, setExpandedJournalEntries] = useState<Set<number>>(new Set());

  useEffect(() => {
    const state = location.state as {
      activeTab?: AccountTab;
      referenceTransactionId?: number;
      plAccountId?: number;
      startDate?: string;
      endDate?: string;
    } | null;
    if (state?.activeTab) {
      setActiveTab(state.activeTab);
    }
    if (state?.referenceTransactionId) {
      setJournalReferenceFilter(state.referenceTransactionId);
    }
    if (state?.startDate) {
      setStartDate(state.startDate);
    }
    if (state?.endDate) {
      setEndDate(state.endDate);
    }
    if (state?.plAccountId) {
      pendingPlAccountIdRef.current = state.plAccountId;
    }
    if (state?.activeTab || state?.referenceTransactionId || state?.plAccountId) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (plView !== 'detail') return;
    setPlView('summary');
    setSelectedPlAccount(null);
  }, [startDate, endDate]);

  useEffect(() => {
    if (!profitAndLoss || pendingPlAccountIdRef.current == null) return;

    const accountId = pendingPlAccountIdRef.current;
    const allAccounts = [
      ...profitAndLoss.income.accounts,
      ...profitAndLoss.expenses.accounts,
    ];
    const match = allAccounts.find((a) => a.account_id === accountId);
    pendingPlAccountIdRef.current = null;

    if (match && parseFloat(match.amount) !== 0) {
      setSelectedPlAccount({
        account_id: match.account_id,
        account_number: match.account_number,
        name: match.name,
      });
      setPlView('detail');
    }
  }, [profitAndLoss]);

  useEffect(() => {
    if (activeTab === 'chart-of-accounts' && canAccessAccounts()) {
      fetchAccounts();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'journal' && canAccessAccounts()) {
      fetchJournalEntries();
      if (accounts.length === 0) {
        getAllAccounts()
          .then(setAccounts)
          .catch(() => { });
      }
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'trial-balance' && canAccessAccounts()) {
      fetchTrialBalance();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'income-statement' && canAccessAccounts()) {
      fetchProfitAndLoss();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'balance-sheet' && canAccessAccounts()) {
      fetchBalanceSheet();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'ar-aging' && canAccessAccounts()) {
      fetchArAging();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'ap-aging' && canAccessAccounts()) {
      fetchApAging();
    }
  }, [activeTab]);

  useEffect(() => {
    selectedArCustomerOptionRef.current = selectedArCustomerOption;
  }, [selectedArCustomerOption]);

  useEffect(() => {
    selectedApVendorOptionRef.current = selectedApVendorOption;
  }, [selectedApVendorOption]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setCustomerSearchLoading(true);
      try {
        const result = await getCustomers({
          search: customerSearchInput.trim() || undefined,
          per_page: 20,
          page: 1,
        });
        for (const customer of result.customers) {
          customerLookupRef.current.set(customer.id, customer);
        }
        const options = result.customers.map(customerToOption);
        const selected = selectedArCustomerOptionRef.current;
        setCustomerOptions(
          selected && !options.some((opt) => String(opt.value) === String(selected.value))
            ? [selected, ...options]
            : options
        );
      } catch {
        // silently ignore — dropdown just shows empty
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchInput]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setVendorSearchLoading(true);
      try {
        const result = await getSuppliers({
          search: vendorSearchInput.trim() || undefined,
          per_page: 20,
          page: 1,
        });
        for (const supplier of result.suppliers) {
          supplierLookupRef.current.set(supplier.id, supplier);
        }
        const options = result.suppliers.map(supplierToOption);
        const selected = selectedApVendorOptionRef.current;
        setVendorOptions(
          selected && !options.some((opt) => String(opt.value) === String(selected.value))
            ? [selected, ...options]
            : options
        );
      } catch {
        // silently ignore — dropdown just shows empty
      } finally {
        setVendorSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [vendorSearchInput]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllAccounts();
      setAccounts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialBalance = async () => {
    try {
      setTrialBalanceLoading(true);
      setTrialBalanceError(null);
      const data = await getTrialBalance({ as_of: asOfDate });
      setTrialBalance(data);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const firstError = Object.values(err.errors).flat()[0];
        setTrialBalanceError(firstError || err.message);
      } else {
        setTrialBalanceError(err instanceof Error ? err.message : 'Failed to fetch trial balance');
      }
      setTrialBalance(null);
    } finally {
      setTrialBalanceLoading(false);
    }
  };

  const fetchProfitAndLoss = async () => {
    try {
      setProfitAndLossLoading(true);
      setProfitAndLossError(null);
      const data = await getProfitAndLoss({
        start_date: startDate,
        end_date: endDate,
        include_zero_balances: includeZeroBalances,
      });
      setProfitAndLoss(data);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const firstError = Object.values(err.errors).flat()[0];
        setProfitAndLossError(firstError || err.message);
      } else {
        setProfitAndLossError(
          err instanceof Error ? err.message : 'Failed to fetch income statement'
        );
      }
      setProfitAndLoss(null);
    } finally {
      setProfitAndLossLoading(false);
    }
  };

  const exportProfitAndLoss = () => {
    if (!profitAndLoss) return;

    const accountRows = (accounts: ProfitAndLossReport['income']['accounts']) =>
      accounts.map((account) => [
        account.account_number,
        account.name,
        account.account_type,
        account.account_sub_type,
        account.amount,
      ]);

    const rows = [
      ['Income Statement', `${profitAndLoss.start_date} to ${profitAndLoss.end_date}`],
      [],
      ['Income'],
      ['Account Number', 'Account Name', 'Type', 'Sub-Type', 'Amount'],
      ...accountRows(profitAndLoss.income.accounts),
      ['Total Income', '', '', '', profitAndLoss.income.total],
      [],
      ['Expenses'],
      ['Account Number', 'Account Name', 'Type', 'Sub-Type', 'Amount'],
      ...accountRows(profitAndLoss.expenses.accounts),
      ['Total Expenses', '', '', '', profitAndLoss.expenses.total],
      [],
      ['Net Income', '', '', '', profitAndLoss.net_income],
    ];

    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `income-statement-${profitAndLoss.start_date}-to-${profitAndLoss.end_date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePlAccountDrillDown = (account: ProfitAndLossReport['income']['accounts'][number]) => {
    if (parseFloat(account.amount) === 0) return;
    setSelectedPlAccount({
      account_id: account.account_id,
      account_number: account.account_number,
      name: account.name,
    });
    setPlView('detail');
  };

  const handlePlBackToSummary = () => {
    setPlView('summary');
    setSelectedPlAccount(null);
  };

  const exportProfitAndLossDetail = async () => {
    if (!selectedPlAccount) return;

    try {
      const data = await getProfitAndLossDetail({
        account_id: selectedPlAccount.account_id,
        start_date: startDate,
        end_date: endDate,
      });
      const account = data.accounts[0];
      if (!account) return;

      const rows = [
        [
          'Income Statement Detail',
          `${selectedPlAccount.account_number} — ${selectedPlAccount.name}`,
        ],
        [
          'Period',
          data.start_date != null ? `${data.start_date} to ${data.end_date}` : `Through ${data.end_date}`,
        ],
        [],
        ['Date', 'Type', 'Num', 'Description', 'Posting Type', 'Amount', 'JE Number'],
        ...account.transactions.map((txn) => [
          txn.txn_date,
          getReportSourceLabel(txn.source),
          txn.source?.number ?? '',
          txn.description ?? '',
          txn.posting_type,
          txn.amount,
          txn.je_number,
        ]),
        [],
        ['Account Total', '', '', '', '', account.amount, ''],
      ];

      const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `income-statement-detail-${selectedPlAccount.account_number}-${startDate}-to-${endDate}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to export detail');
    }
  };

  const formatJournalSource = (entry: JournalEntry) => {
    if (!entry.source) return entry.memo ?? '—';
    const label = SOURCE_TYPE_LABELS[entry.source.type] ?? formatLabel(entry.source.type);
    return `${entry.source.number} (${label})`;
  };

  const fetchJournalEntries = async (page = journalPage) => {
    try {
      setJournalLoading(true);
      setJournalError(null);
      setExpandedJournalEntries(new Set());

      const params: Parameters<typeof getJournalEntries>[0] = {
        start_date: startDate,
        end_date: endDate,
        per_page: 50,
        page,
      };

      if (journalAccountFilter) {
        params.account_id = Number(journalAccountFilter);
      }
      if (journalAutoFilter === 'true') {
        params.is_auto_generated = true;
      } else if (journalAutoFilter === 'false') {
        params.is_auto_generated = false;
      }
      if (journalReferenceFilter) {
        params.reference_transaction_id = journalReferenceFilter;
      }

      const { entries, meta } = await getJournalEntries(params);
      setJournalEntries(entries);
      setJournalMeta(meta);
      setJournalPage(meta.current_page);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const firstError = Object.values(err.errors).flat()[0];
        setJournalError(firstError || err.message);
      } else {
        setJournalError(err instanceof Error ? err.message : 'Failed to fetch journal entries');
      }
      setJournalEntries([]);
      setJournalMeta(null);
    } finally {
      setJournalLoading(false);
    }
  };

  const exportJournalEntries = () => {
    if (journalEntries.length === 0) return;

    const rows = [
      ['Journal Entries', `${startDate} to ${endDate}`],
      [],
      [
        'JE Number',
        'Date',
        'Memo',
        'Source',
        'Account Number',
        'Account Name',
        'Description',
        'Debit',
        'Credit',
      ],
      ...journalEntries.flatMap((entry) =>
        entry.lines.map((line) => [
          entry.je_number,
          entry.txn_date,
          entry.memo ?? '',
          formatJournalSource(entry),
          line.account_number,
          line.account_name,
          line.description ?? '',
          line.posting_type === 'DEBIT' ? line.amount : '',
          line.posting_type === 'CREDIT' ? line.amount : '',
        ])
      ),
    ];

    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `journal-entries-${startDate}-to-${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleJournalEntry = (entryId: number) => {
    setExpandedJournalEntries((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const fetchBalanceSheet = async () => {
    try {
      setBalanceSheetLoading(true);
      setBalanceSheetError(null);
      const data = await getBalanceSheet({
        as_of: asOfDate,
        include_zero_balances: includeZeroBalances,
      });
      setBalanceSheet(data);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const firstError = Object.values(err.errors).flat()[0];
        setBalanceSheetError(firstError || err.message);
      } else {
        setBalanceSheetError(err instanceof Error ? err.message : 'Failed to fetch balance sheet');
      }
      setBalanceSheet(null);
    } finally {
      setBalanceSheetLoading(false);
    }
  };

  const balanceSheetAccountKey = (account: BalanceSheetAccount, index: number) =>
    account.account_id ?? `synthetic-${account.account_sub_type}-${index}`;

  const balanceSheetAccountRows = (accounts: BalanceSheetAccount[]) =>
    accounts.map((account) => [
      account.account_number ?? '',
      account.name,
      account.account_type,
      account.account_sub_type,
      account.amount,
    ]);

  const exportBalanceSheet = () => {
    if (!balanceSheet) return;

    const rows = [
      ['Balance Sheet', `As of ${balanceSheet.as_of}`],
      ['Balanced', balanceSheet.is_balanced ? 'Yes' : 'No'],
      [],
      ['Assets'],
      ['Account Number', 'Account Name', 'Type', 'Sub-Type', 'Amount'],
      ...balanceSheetAccountRows(balanceSheet.assets.accounts),
      ['Total Assets', '', '', '', balanceSheet.assets.total],
      [],
      ['Liabilities'],
      ['Account Number', 'Account Name', 'Type', 'Sub-Type', 'Amount'],
      ...balanceSheetAccountRows(balanceSheet.liabilities.accounts),
      ['Total Liabilities', '', '', '', balanceSheet.liabilities.total],
      [],
      ['Equity'],
      ['Account Number', 'Account Name', 'Type', 'Sub-Type', 'Amount'],
      ...balanceSheetAccountRows(balanceSheet.equity.accounts),
      ['Current Period Earnings', '', '', '', balanceSheet.equity.current_period_earnings],
      ['Total Equity', '', '', '', balanceSheet.equity.total],
      [],
      ['Total Assets', '', '', '', balanceSheet.totals.assets],
      ['Total Liabilities & Equity', '', '', '', balanceSheet.totals.liabilities_and_equity],
    ];

    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `balance-sheet-${balanceSheet.as_of}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const fetchArAging = async () => {
    try {
      setArAgingLoading(true);
      setArAgingError(null);
      setExpandedArCustomers(new Set());
      const data = await getArAging({
        as_of: asOfDate,
        customer_id: arCustomerFilter ? Number(arCustomerFilter) : undefined,
      });
      setArAging(data);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const firstError = Object.values(err.errors).flat()[0];
        setArAgingError(firstError || err.message);
      } else {
        setArAgingError(err instanceof Error ? err.message : 'Failed to fetch AR aging report');
      }
      setArAging(null);
    } finally {
      setArAgingLoading(false);
    }
  };

  const fetchApAging = async () => {
    try {
      setApAgingLoading(true);
      setApAgingError(null);
      setExpandedApVendors(new Set());
      const data = await getApAging({
        as_of: asOfDate,
        vendor_id: apVendorFilter ? Number(apVendorFilter) : undefined,
      });
      setApAging(data);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const firstError = Object.values(err.errors).flat()[0];
        setApAgingError(firstError || err.message);
      } else {
        setApAgingError(err instanceof Error ? err.message : 'Failed to fetch AP aging report');
      }
      setApAging(null);
    } finally {
      setApAgingLoading(false);
    }
  };

  const handleArCustomerFilterChange = async (value: number | string) => {
    const idStr = value ? String(value) : '';
    setArCustomerFilter(idStr);

    if (!idStr) {
      setSelectedArCustomerOption(null);
      selectedArCustomerOptionRef.current = null;
      return;
    }

    const id = Number(idStr);
    let customer = customerLookupRef.current.get(id);
    if (!customer) {
      customer = await getCustomerById(id).catch(() => undefined);
      if (customer) {
        customerLookupRef.current.set(id, customer);
      }
    }

    if (customer) {
      const option = customerToOption(customer);
      setSelectedArCustomerOption(option);
      selectedArCustomerOptionRef.current = option;
      setCustomerOptions((prev) =>
        prev.some((opt) => String(opt.value) === idStr) ? prev : [option, ...prev]
      );
    }
  };

  const handleApVendorFilterChange = async (value: number | string) => {
    const idStr = value ? String(value) : '';
    setApVendorFilter(idStr);

    if (!idStr) {
      setSelectedApVendorOption(null);
      selectedApVendorOptionRef.current = null;
      return;
    }

    const id = Number(idStr);
    let supplier = supplierLookupRef.current.get(id);
    if (!supplier) {
      supplier = await getSupplierById(id).catch(() => undefined);
      if (supplier) {
        supplierLookupRef.current.set(id, supplier);
      }
    }

    if (supplier) {
      const option = supplierToOption(supplier);
      setSelectedApVendorOption(option);
      selectedApVendorOptionRef.current = option;
      setVendorOptions((prev) =>
        prev.some((opt) => String(opt.value) === idStr) ? prev : [option, ...prev]
      );
    }
  };

  const agingTotalsRow = (totals: AgingBucketTotals) =>
    AGING_BUCKET_COLUMNS.map((col) => totals[col.key]);

  const exportArAging = () => {
    if (!arAging) return;

    const rows = [
      ['AR Aging', `As of ${arAging.as_of}`],
      [],
      ['Customer', ...AGING_BUCKET_COLUMNS.map((col) => col.label)],
      ...arAging.customers.map((customer) => [
        customer.display_name,
        ...agingTotalsRow(customer.totals),
      ]),
      ['Totals', ...agingTotalsRow(arAging.totals)],
      [],
      ['Customer', 'Invoice #', 'Txn Date', 'Due Date', 'Total', 'Balance', 'Days Overdue', 'Bucket'],
      ...arAging.customers.flatMap((customer) =>
        customer.invoices.map((invoice) => [
          customer.display_name,
          invoice.invoice_number,
          invoice.txn_date,
          invoice.due_date,
          invoice.total_amount,
          invoice.balance,
          String(invoice.days_overdue),
          AGING_BUCKET_LABELS[invoice.bucket] ?? invoice.bucket,
        ])
      ),
    ];

    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ar-aging-${arAging.as_of}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportApAging = () => {
    if (!apAging) return;

    const rows = [
      ['AP Aging', `As of ${apAging.as_of}`],
      [],
      ['Vendor', ...AGING_BUCKET_COLUMNS.map((col) => col.label)],
      ...apAging.vendors.map((vendor) => [vendor.display_name, ...agingTotalsRow(vendor.totals)]),
      ['Totals', ...agingTotalsRow(apAging.totals)],
      [],
      ['Vendor', 'Bill #', 'Txn Date', 'Due Date', 'Total', 'Balance', 'Days Overdue', 'Bucket'],
      ...apAging.vendors.flatMap((vendor) =>
        vendor.bills.map((bill) => [
          vendor.display_name,
          bill.bill_number,
          bill.txn_date,
          bill.due_date,
          bill.total_amount,
          bill.balance,
          String(bill.days_overdue),
          AGING_BUCKET_LABELS[bill.bucket] ?? bill.bucket,
        ])
      ),
    ];

    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ap-aging-${apAging.as_of}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleArCustomer = (customerId: number) => {
    setExpandedArCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(customerId)) {
        next.delete(customerId);
      } else {
        next.add(customerId);
      }
      return next;
    });
  };

  const toggleApVendor = (vendorId: number) => {
    setExpandedApVendors((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) {
        next.delete(vendorId);
      } else {
        next.add(vendorId);
      }
      return next;
    });
  };

  const exportTrialBalance = () => {
    if (!trialBalance) return;

    const rows = [
      ['Trial Balance', `As of ${trialBalance.as_of}`],
      [],
      ['Account Number', 'Account Name', 'Type', 'Sub-Type', 'Debit', 'Credit'],
      ...trialBalance.accounts.map((account) => [
        account.account_number,
        account.name,
        account.account_type,
        account.account_sub_type,
        account.debit,
        account.credit,
      ]),
      [],
      ['Totals', '', '', '', trialBalance.totals.debit, trialBalance.totals.credit],
    ];

    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `trial-balance-${trialBalance.as_of}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setFormData(defaultFormData());
    setFieldErrors({});
    setModalError(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (account: GlAccount) => {
    setEditingAccount(account);
    setFormData({
      account_number: account.account_number,
      name: account.name,
      account_type: account.account_type,
      account_sub_type: account.account_sub_type,
      parent_account_id: account.parent_account_id,
      active: account.active,
    });
    setFieldErrors({});
    setModalError(null);
    setShowEditModal(true);
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setModalError(null);
    setFieldErrors({});

    try {
      setSubmitting(true);
      await createAccount({
        account_number: formData.account_number.trim(),
        name: formData.name.trim(),
        account_type: formData.account_type,
        account_sub_type: formData.account_sub_type,
        parent_account_id: formData.parent_account_id || null,
        active: formData.active,
      });
      setShowCreateModal(false);
      resetForm();
      await fetchAccounts();
      toast.success('Account created successfully!');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setModalError(err.message);
      } else {
        toastApiError(err, 'Failed to create account');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingAccount) return;

    setModalError(null);
    setFieldErrors({});

    const updateData: UpdateAccountData = {
      account_number: formData.account_number.trim(),
      name: formData.name.trim(),
      account_type: formData.account_type,
      account_sub_type: formData.account_sub_type,
      parent_account_id: formData.parent_account_id ?? null,
      active: formData.active,
    };

    try {
      setSubmitting(true);
      await updateAccount(editingAccount.id, updateData);
      setShowEditModal(false);
      setEditingAccount(null);
      resetForm();
      await fetchAccounts();
      toast.success('Account updated successfully!');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setModalError(err.message);
      } else {
        toastApiError(err, 'Failed to update account');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (accountId: number) => {
    if (!canDeleteAccount()) {
      toast.error('Only administrators can delete accounts.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this account?')) {
      return;
    }

    try {
      await deleteAccount(accountId);
      await fetchAccounts();
      toast.success('Account deleted successfully!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete account');
    }
  };

  const filteredAccounts = accounts.filter((account) => {
    const query = searchQuery.toLowerCase();
    return (
      account.account_number.toLowerCase().includes(query) ||
      account.name.toLowerCase().includes(query) ||
      account.account_type.toLowerCase().includes(query) ||
      account.account_sub_type.toLowerCase().includes(query) ||
      account.parent?.name.toLowerCase().includes(query)
    );
  });

  const parentOptions = accounts.filter(
    (account) => !editingAccount || account.id !== editingAccount.id
  );

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  const renderAccountForm = (onSubmit: (e: FormEvent) => void, submitLabel: string) => (
    <form onSubmit={onSubmit} className="space-y-4">
      {modalError && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{modalError}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Account Number <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            maxLength={20}
            value={formData.account_number}
            onChange={(e) => setFormData((prev) => ({ ...prev, account_number: e.target.value }))}
            className={`w-full border rounded-lg px-3 py-2 text-sm ${fieldError('account_number') ? 'border-red-300' : 'border-gray-300'
              }`}
            placeholder="1000"
          />
          {fieldError('account_number') && (
            <p className="mt-1 text-sm text-red-600">{fieldError('account_number')}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Account Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            maxLength={200}
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            className={`w-full border rounded-lg px-3 py-2 text-sm ${fieldError('name') ? 'border-red-300' : 'border-gray-300'
              }`}
            placeholder="Cash on Hand"
          />
          {fieldError('name') && <p className="mt-1 text-sm text-red-600">{fieldError('name')}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
          <select
            value={formData.account_type}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, account_type: e.target.value as AccountType }))
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {ACCOUNT_TYPES.map((type) => (
              <option key={type} value={type}>
                {formatLabel(type)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Type</label>
          <select
            value={formData.account_sub_type}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                account_sub_type: e.target.value as AccountSubType,
              }))
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {ACCOUNT_SUB_TYPES.map((subType) => (
              <option key={subType} value={subType}>
                {formatLabel(subType)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Parent Account</label>
        <select
          value={formData.parent_account_id ?? ''}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              parent_account_id: e.target.value ? Number(e.target.value) : null,
            }))
          }
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">None (top-level)</option>
          {parentOptions.map((account) => (
            <option key={account.id} value={account.id}>
              {account.account_number} — {account.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center">
        <input
          id="account-active"
          type="checkbox"
          checked={formData.active ?? true}
          onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
          className="h-4 w-4 rounded border-gray-300"
        />
        <label htmlFor="account-active" className="ml-2 text-sm text-gray-700">
          Active account
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setEditingAccount(null);
            resetForm();
          }}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-slate-800 text-white rounded-md text-sm disabled:opacity-60"
        >
          {submitting ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  );

  const renderJournal = () => {
    if (!canAccessAccounts()) {
      return (
        <div className="p-8 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-amber-500 mb-3" />
          <p className="text-gray-700 font-medium">Access restricted</p>
          <p className="text-sm text-gray-500 mt-1">
            Only administrators and accountants can view journal entries.
          </p>
        </div>
      );
    }

    if (journalLoading) {
      return (
        <div className="p-12 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      );
    }

    return (
      <div className="p-4 md:p-6">
        {journalError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <p className="text-red-800 text-sm">{journalError}</p>
            </div>
            <button
              onClick={() => fetchJournalEntries()}
              className="text-sm text-red-700 underline shrink-0"
            >
              Retry
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Journal</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {startDate} to {endDate}
              {journalMeta && (
                <span className="ml-2">
                  · {journalMeta.total} {journalMeta.total === 1 ? 'entry' : 'entries'}
                </span>
              )}
            </p>
          </div>
        </div>

        {journalEntries.length === 0 && !journalError ? (
          <div className="py-12 text-center text-gray-500">
            No journal entries for the selected filters.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left font-medium text-gray-600 w-8" />
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">JE Number</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Reference</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Memo</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Debit</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {journalEntries.map((entry) => {
                    const isExpanded = expandedJournalEntries.has(entry.id);
                    return (
                      <Fragment key={entry.id}>
                        <tr
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleJournalEntry(entry.id)}
                        >
                          <td className="px-4 py-3 text-gray-400">
                            {entry.lines.length > 0 ? (
                              isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )
                            ) : null}
                          </td>
                          <td className="px-4 py-3 text-gray-900">{entry.txn_date}</td>
                          <td className="px-4 py-3 font-mono font-medium text-gray-900">
                            {entry.je_number}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            <div className="flex flex-col gap-0.5">
                              <span>{formatJournalSource(entry)}</span>
                              {entry.is_auto_generated && (
                                <span className="text-xs text-gray-400">System generated</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{entry.memo ?? '—'}</td>
                          <td className="px-4 py-3 text-right font-mono text-gray-900">
                            {formatCurrency(entry.totals.debit)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-gray-900">
                            {formatCurrency(entry.totals.credit)}
                          </td>
                        </tr>
                        {isExpanded &&
                          entry.lines.map((line) => (
                            <tr key={`${entry.id}-line-${line.line_number}`} className="bg-gray-50/60">
                              <td />
                              <td />
                              <td className="px-4 py-2 pl-8 text-gray-500 font-mono text-xs">
                                Line {line.line_number}
                              </td>
                              <td className="px-4 py-2 text-gray-700" colSpan={2}>
                                <span className="font-mono">{line.account_number}</span>
                                <span className="mx-2 text-gray-300">—</span>
                                <span>{line.account_name}</span>
                                {line.description && (
                                  <span className="block text-xs text-gray-500 mt-0.5">
                                    {line.description}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-right font-mono text-gray-700">
                                {line.posting_type === 'DEBIT' ? formatCurrency(line.amount) : '—'}
                              </td>
                              <td className="px-4 py-2 text-right font-mono text-gray-700">
                                {line.posting_type === 'CREDIT' ? formatCurrency(line.amount) : '—'}
                              </td>
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {journalMeta && journalMeta.last_page > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Page {journalMeta.current_page} of {journalMeta.last_page}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fetchJournalEntries(journalMeta.current_page - 1)}
                    disabled={journalMeta.current_page <= 1 || journalLoading}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchJournalEntries(journalMeta.current_page + 1)}
                    disabled={journalMeta.current_page >= journalMeta.last_page || journalLoading}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderTrialBalance = () => {
    if (!canAccessAccounts()) {
      return (
        <div className="p-8 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-amber-500 mb-3" />
          <p className="text-gray-700 font-medium">Access restricted</p>
          <p className="text-sm text-gray-500 mt-1">
            Only administrators and accountants can view the trial balance report.
          </p>
        </div>
      );
    }

    if (trialBalanceLoading) {
      return (
        <div className="p-12 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      );
    }

    return (
      <div className="p-4 md:p-6">
        {trialBalanceError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <p className="text-red-800 text-sm">{trialBalanceError}</p>
            </div>
            <button onClick={fetchTrialBalance} className="text-sm text-red-700 underline shrink-0">
              Retry
            </button>
          </div>
        )}

        {trialBalance && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Trial Balance</h2>
                <p className="text-sm text-gray-500 mt-0.5">As of {trialBalance.as_of}</p>
              </div>
              <span
                className={`inline-flex items-center gap-1.5 self-start px-3 py-1 text-xs font-medium rounded-full ${trialBalance.is_balanced
                    ? 'bg-green-100 text-green-800'
                    : 'bg-amber-100 text-amber-800'
                  }`}
              >
                {trialBalance.is_balanced ? (
                  <CheckCircle className="h-3.5 w-3.5" />
                ) : (
                  <AlertCircle className="h-3.5 w-3.5" />
                )}
                {trialBalance.is_balanced ? 'Balanced' : 'Out of balance'}
              </span>
            </div>

            <div className="overflow-x-auto">
              {trialBalance.accounts.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  No accounts with balances for the selected date.
                </div>
              ) : (
                <table className="w-full min-w-[800px] text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Account Number</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Account Name</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Sub-Type</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Debit</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Credit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {trialBalance.accounts.map((account) => (
                      <tr key={account.account_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-gray-900">{account.account_number}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">{account.name}</td>
                        <td className="px-4 py-3 text-gray-600">{formatLabel(account.account_type)}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {formatLabel(account.account_sub_type)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-gray-900">
                          {parseFloat(account.debit) > 0 ? formatCurrency(account.debit) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-gray-900">
                          {parseFloat(account.credit) > 0 ? formatCurrency(account.credit) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
                      <td colSpan={4} className="px-4 py-3 text-right text-gray-900">
                        Totals
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-gray-900">
                        {formatCurrency(trialBalance.totals.debit)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-gray-900">
                        {formatCurrency(trialBalance.totals.credit)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </>
        )}

        {!trialBalance && !trialBalanceError && (
          <div className="py-12 text-center text-gray-500">
            Select a date and click &quot;Run Report&quot; to generate the trial balance.
          </div>
        )}
      </div>
    );
  };

  const renderProfitAndLossSection = (
    title: string,
    accounts: ProfitAndLossReport['income']['accounts'],
    total: string,
    emptyMessage: string
  ) => (
    <div className="mb-8">
      <h3 className="text-base font-semibold text-gray-900 mb-3">{title}</h3>
      {accounts.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">{emptyMessage}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-medium text-gray-600">Account Number</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Account Name</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Sub-Type</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {accounts.map((account) => (
                <tr key={account.account_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-gray-900">{account.account_number}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{account.name}</td>
                  <td className="px-4 py-3 text-gray-600">{formatLabel(account.account_type)}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatLabel(account.account_sub_type)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-gray-900">
                    {parseFloat(account.amount) !== 0 ? (
                      <button
                        type="button"
                        onClick={() => handlePlAccountDrillDown(account)}
                        className="text-blue-700 hover:underline cursor-pointer"
                        title="View transactions"
                      >
                        {formatCurrency(account.amount)}
                      </button>
                    ) : (
                      formatCurrency(account.amount)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
                <td colSpan={4} className="px-4 py-3 text-right text-gray-900">
                  Total {title}
                </td>
                <td className="px-4 py-3 text-right font-mono text-gray-900">
                  {formatCurrency(total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );

  const renderBalanceSheetSection = (
    title: string,
    accounts: BalanceSheetAccount[],
    total: string,
    emptyMessage: string
  ) => (
    <div className="mb-8">
      <h3 className="text-base font-semibold text-gray-900 mb-3">{title}</h3>
      {accounts.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">{emptyMessage}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-medium text-gray-600">Account Number</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Account Name</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Sub-Type</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {accounts.map((account, index) => (
                <tr key={balanceSheetAccountKey(account, index)} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-gray-900">
                    {account.account_number ?? '—'}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{account.name}</td>
                  <td className="px-4 py-3 text-gray-600">{formatLabel(account.account_type)}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatLabel(account.account_sub_type)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-gray-900">
                    {formatCurrency(account.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
                <td colSpan={4} className="px-4 py-3 text-right text-gray-900">
                  Total {title}
                </td>
                <td className="px-4 py-3 text-right font-mono text-gray-900">
                  {formatCurrency(total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );

  const renderBalanceSheet = () => {
    if (!canAccessAccounts()) {
      return (
        <div className="p-8 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-amber-500 mb-3" />
          <p className="text-gray-700 font-medium">Access restricted</p>
          <p className="text-sm text-gray-500 mt-1">
            Only administrators and accountants can view the balance sheet.
          </p>
        </div>
      );
    }

    if (balanceSheetLoading) {
      return (
        <div className="p-12 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      );
    }

    return (
      <div className="p-4 md:p-6">
        {balanceSheetError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <p className="text-red-800 text-sm">{balanceSheetError}</p>
            </div>
            <button onClick={fetchBalanceSheet} className="text-sm text-red-700 underline shrink-0">
              Retry
            </button>
          </div>
        )}

        {balanceSheet && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Balance Sheet</h2>
                <p className="text-sm text-gray-500 mt-0.5">As of {balanceSheet.as_of}</p>
              </div>
              <span
                className={`inline-flex items-center gap-1.5 self-start px-3 py-1 text-xs font-medium rounded-full ${balanceSheet.is_balanced
                    ? 'bg-green-100 text-green-800'
                    : 'bg-amber-100 text-amber-800'
                  }`}
              >
                {balanceSheet.is_balanced ? (
                  <CheckCircle className="h-3.5 w-3.5" />
                ) : (
                  <AlertCircle className="h-3.5 w-3.5" />
                )}
                {balanceSheet.is_balanced ? 'Balanced' : 'Out of balance'}
              </span>
            </div>

            {renderBalanceSheetSection(
              'Assets',
              balanceSheet.assets.accounts,
              balanceSheet.assets.total,
              'No asset balances for the selected date.'
            )}

            {renderBalanceSheetSection(
              'Liabilities',
              balanceSheet.liabilities.accounts,
              balanceSheet.liabilities.total,
              'No liability balances for the selected date.'
            )}

            {renderBalanceSheetSection(
              'Equity',
              balanceSheet.equity.accounts,
              balanceSheet.equity.total,
              'No equity balances for the selected date.'
            )}

            <div className="border-t-2 border-gray-300 pt-4 space-y-2">
              <div className="flex justify-between items-center px-4 py-3 bg-gray-50 rounded-lg font-semibold">
                <span className="text-gray-900">Total Assets</span>
                <span className="font-mono text-gray-900">
                  {formatCurrency(balanceSheet.totals.assets)}
                </span>
              </div>
              <div className="flex justify-between items-center px-4 py-3 bg-gray-50 rounded-lg font-semibold">
                <span className="text-gray-900">Total Liabilities &amp; Equity</span>
                <span className="font-mono text-gray-900">
                  {formatCurrency(balanceSheet.totals.liabilities_and_equity)}
                </span>
              </div>
            </div>
          </>
        )}

        {!balanceSheet && !balanceSheetError && (
          <div className="py-12 text-center text-gray-500">
            Select a date and click &quot;Run Report&quot; to generate the balance sheet.
          </div>
        )}
      </div>
    );
  };

  const renderIncomeStatement = () => {
    if (!canAccessAccounts()) {
      return (
        <div className="p-8 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-amber-500 mb-3" />
          <p className="text-gray-700 font-medium">Access restricted</p>
          <p className="text-sm text-gray-500 mt-1">
            Only administrators and accountants can view the income statement.
          </p>
        </div>
      );
    }

    if (plView === 'detail' && selectedPlAccount) {
      return (
        <ProfitAndLossAccountDetail
          accountId={selectedPlAccount.account_id}
          accountNumber={selectedPlAccount.account_number}
          accountName={selectedPlAccount.name}
          startDate={startDate}
          endDate={endDate}
          onBack={handlePlBackToSummary}
        />
      );
    }

    if (profitAndLossLoading) {
      return (
        <div className="p-12 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      );
    }

    const netIncome = profitAndLoss ? parseFloat(profitAndLoss.net_income) : 0;

    return (
      <div className="p-4 md:p-6">
        {profitAndLossError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <p className="text-red-800 text-sm">{profitAndLossError}</p>
            </div>
            <button onClick={fetchProfitAndLoss} className="text-sm text-red-700 underline shrink-0">
              Retry
            </button>
          </div>
        )}

        {profitAndLoss && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Income Statement</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {profitAndLoss.start_date} to {profitAndLoss.end_date}
                </p>
              </div>
              <span
                className={`inline-flex items-center gap-1.5 self-start px-3 py-1 text-xs font-medium rounded-full ${netIncome >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
              >
                Net Income: {formatCurrency(profitAndLoss.net_income)}
              </span>
            </div>

            {renderProfitAndLossSection(
              'Income',
              profitAndLoss.income.accounts,
              profitAndLoss.income.total,
              'No income activity for the selected period.'
            )}

            {renderProfitAndLossSection(
              'Expenses',
              profitAndLoss.expenses.accounts,
              profitAndLoss.expenses.total,
              'No expense activity for the selected period.'
            )}

            <div className="border-t-2 border-gray-300 pt-4">
              <div className="flex justify-between items-center px-4 py-3 bg-gray-50 rounded-lg font-semibold">
                <span className="text-gray-900">Net Income</span>
                <span
                  className={`font-mono ${netIncome >= 0 ? 'text-green-700' : 'text-red-700'
                    }`}
                >
                  {formatCurrency(profitAndLoss.net_income)}
                </span>
              </div>
            </div>
          </>
        )}

        {!profitAndLoss && !profitAndLossError && (
          <div className="py-12 text-center text-gray-500">
            Select a date range and click &quot;Run Report&quot; to generate the income statement.
          </div>
        )}
      </div>
    );
  };

  const renderAgingBucketCells = (totals: AgingBucketTotals, bold = false) =>
    AGING_BUCKET_COLUMNS.map((col) => (
      <td
        key={col.key}
        className={`px-3 py-3 text-right font-mono text-gray-900 ${bold ? 'font-semibold' : ''}`}
      >
        {formatAgingAmount(totals[col.key])}
      </td>
    ));

  const renderArAging = () => {
    if (!canAccessAccounts()) {
      return (
        <div className="p-8 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-amber-500 mb-3" />
          <p className="text-gray-700 font-medium">Access restricted</p>
          <p className="text-sm text-gray-500 mt-1">
            Only administrators and accountants can view the AR aging report.
          </p>
        </div>
      );
    }

    if (arAgingLoading) {
      return (
        <div className="p-12 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      );
    }

    return (
      <div className="p-4 md:p-6">
        {arAgingError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <p className="text-red-800 text-sm">{arAgingError}</p>
            </div>
            <button onClick={fetchArAging} className="text-sm text-red-700 underline shrink-0">
              Retry
            </button>
          </div>
        )}

        {arAging && (
          <>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">AR Aging</h2>
              <p className="text-sm text-gray-500 mt-0.5">As of {arAging.as_of}</p>
            </div>

            <div className="overflow-x-auto">
              {arAging.customers.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  No outstanding receivables for the selected date.
                </div>
              ) : (
                <table className="w-full min-w-[900px] text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left font-medium text-gray-600 w-8" />
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Customer</th>
                      {AGING_BUCKET_COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          className="px-3 py-3 text-right font-medium text-gray-600 whitespace-nowrap"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {arAging.customers.map((customer) => {
                      const isExpanded = expandedArCustomers.has(customer.customer_id);
                      return (
                        <Fragment key={customer.customer_id}>
                          <tr
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleArCustomer(customer.customer_id)}
                          >
                            <td className="px-4 py-3 text-gray-400">
                              {customer.invoices.length > 0 ? (
                                isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )
                              ) : null}
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {customer.display_name}
                            </td>
                            {renderAgingBucketCells(customer.totals)}
                          </tr>
                          {isExpanded &&
                            customer.invoices.map((invoice) => (
                              <tr
                                key={`inv-${invoice.invoice_id}`}
                                className="bg-gray-50/60 text-gray-600"
                              >
                                <td />
                                <td className="px-4 py-2 pl-10">
                                  <span className="font-mono">{invoice.invoice_number}</span>
                                  <span className="mx-2 text-gray-300">|</span>
                                  <span className="text-xs">
                                    Due {invoice.due_date}
                                    {invoice.days_overdue > 0 && (
                                      <span className="text-amber-600 ml-1">
                                        ({invoice.days_overdue}d overdue)
                                      </span>
                                    )}
                                  </span>
                                </td>
                                {AGING_BUCKET_COLUMNS.map((col) => (
                                  <td key={col.key} className="px-3 py-2 text-right font-mono">
                                    {invoice.bucket === col.key
                                      ? formatCurrency(invoice.balance)
                                      : '—'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
                      <td />
                      <td className="px-4 py-3 text-gray-900">Totals</td>
                      {renderAgingBucketCells(arAging.totals, true)}
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </>
        )}

        {!arAging && !arAgingError && (
          <div className="py-12 text-center text-gray-500">
            Select a date and click &quot;Run Report&quot; to generate the AR aging report.
          </div>
        )}
      </div>
    );
  };

  const renderApAging = () => {
    if (!canAccessAccounts()) {
      return (
        <div className="p-8 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-amber-500 mb-3" />
          <p className="text-gray-700 font-medium">Access restricted</p>
          <p className="text-sm text-gray-500 mt-1">
            Only administrators and accountants can view the AP aging report.
          </p>
        </div>
      );
    }

    if (apAgingLoading) {
      return (
        <div className="p-12 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      );
    }

    return (
      <div className="p-4 md:p-6">
        {apAgingError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <p className="text-red-800 text-sm">{apAgingError}</p>
            </div>
            <button onClick={fetchApAging} className="text-sm text-red-700 underline shrink-0">
              Retry
            </button>
          </div>
        )}

        {apAging && (
          <>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">AP Aging</h2>
              <p className="text-sm text-gray-500 mt-0.5">As of {apAging.as_of}</p>
            </div>

            <div className="overflow-x-auto">
              {apAging.vendors.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  No outstanding payables for the selected date.
                </div>
              ) : (
                <table className="w-full min-w-[900px] text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left font-medium text-gray-600 w-8" />
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Vendor</th>
                      {AGING_BUCKET_COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          className="px-3 py-3 text-right font-medium text-gray-600 whitespace-nowrap"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {apAging.vendors.map((vendor) => {
                      const isExpanded = expandedApVendors.has(vendor.vendor_id);
                      return (
                        <Fragment key={vendor.vendor_id}>
                          <tr
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleApVendor(vendor.vendor_id)}
                          >
                            <td className="px-4 py-3 text-gray-400">
                              {vendor.bills.length > 0 ? (
                                isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )
                              ) : null}
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {vendor.display_name}
                            </td>
                            {renderAgingBucketCells(vendor.totals)}
                          </tr>
                          {isExpanded &&
                            vendor.bills.map((bill) => (
                              <tr key={`bill-${bill.bill_id}`} className="bg-gray-50/60 text-gray-600">
                                <td />
                                <td className="px-4 py-2 pl-10">
                                  <span className="font-mono">{bill.bill_number}</span>
                                  <span className="mx-2 text-gray-300">|</span>
                                  <span className="text-xs">
                                    Due {bill.due_date}
                                    {bill.days_overdue > 0 && (
                                      <span className="text-amber-600 ml-1">
                                        ({bill.days_overdue}d overdue)
                                      </span>
                                    )}
                                  </span>
                                </td>
                                {AGING_BUCKET_COLUMNS.map((col) => (
                                  <td key={col.key} className="px-3 py-2 text-right font-mono">
                                    {bill.bucket === col.key ? formatCurrency(bill.balance) : '—'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
                      <td />
                      <td className="px-4 py-3 text-gray-900">Totals</td>
                      {renderAgingBucketCells(apAging.totals, true)}
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </>
        )}

        {!apAging && !apAgingError && (
          <div className="py-12 text-center text-gray-500">
            Select a date and click &quot;Run Report&quot; to generate the AP aging report.
          </div>
        )}
      </div>
    );
  };

  const renderChartOfAccounts = () => {
    if (!canAccessAccounts()) {
      return (
        <div className="p-8 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-amber-500 mb-3" />
          <p className="text-gray-700 font-medium">Access restricted</p>
          <p className="text-sm text-gray-500 mt-1">
            Only administrators and accountants can view the chart of accounts.
          </p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="p-12 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      );
    }

    return (
      <div className="p-4 md:p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
            <button onClick={fetchAccounts} className="text-sm text-red-700 underline shrink-0">
              Retry
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Chart of Accounts</h2>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-slate-800 focus:border-slate-400"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-medium text-gray-600">Account Number</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Account Name</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Sub-Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Parent</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredAccounts.map((account) => (
                <tr key={account.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-gray-900">{account.account_number}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{account.name}</td>
                  <td className="px-4 py-3 text-gray-600">{formatLabel(account.account_type)}</td>
                  <td className="px-4 py-3 text-gray-600">{formatLabel(account.account_sub_type)}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {account.parent
                      ? `${account.parent.account_number} — ${account.parent.name}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${account.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                        }`}
                    >
                      {account.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEditModal(account)}
                        className="text-slate-600 hover:text-slate-900"
                        title="Edit account"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {canDeleteAccount() && (
                        <button
                          onClick={() => handleDelete(account.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete account"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredAccounts.length === 0 && (
            <div className="py-12 text-center text-gray-500">
              {searchQuery
                ? 'No accounts match your search.'
                : 'No accounts yet. Click "Add Account" to build your chart of accounts.'}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen bg-gray-100 flex w-full overflow-hidden">
      <Sidebar />
      <main className="flex-1 p-4 md:p-6 overflow-y-auto min-w-0">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Accounts</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage journal entries, financial statements, and your chart of accounts.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="flex flex-wrap gap-1 p-2 border-b border-gray-100">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive
                      ? 'bg-slate-800 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 bg-gray-50/80">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {(activeTab === 'trial-balance' ||
                activeTab === 'balance-sheet' ||
                activeTab === 'ar-aging' ||
                activeTab === 'ap-aging') && (
                  <>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="date"
                        value={asOfDate}
                        onChange={(e) => setAsOfDate(e.target.value)}
                        className="pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
                      />
                    </div>
                    {activeTab === 'ar-aging' && (
                      <SearchableSelect
                        className="min-w-[180px]"
                        options={customerOptions}
                        value={arCustomerFilter ? Number(arCustomerFilter) : ''}
                        onChange={(value) => void handleArCustomerFilterChange(value)}
                        placeholder="All customers"
                        searchPlaceholder="Search customers..."
                        clearValue=""
                        onSearchChange={setCustomerSearchInput}
                        isLoading={customerSearchLoading}
                      />
                    )}
                    {activeTab === 'ap-aging' && (
                      <SearchableSelect
                        className="min-w-[180px]"
                        options={vendorOptions}
                        value={apVendorFilter ? Number(apVendorFilter) : ''}
                        onChange={(value) => void handleApVendorFilterChange(value)}
                        placeholder="All vendors"
                        searchPlaceholder="Search vendors..."
                        clearValue=""
                        onSearchChange={setVendorSearchInput}
                        isLoading={vendorSearchLoading}
                      />
                    )}
                    {activeTab === 'balance-sheet' && (
                      <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={includeZeroBalances}
                          onChange={(e) => setIncludeZeroBalances(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        Include zero balances
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (activeTab === 'trial-balance') fetchTrialBalance();
                        else if (activeTab === 'balance-sheet') fetchBalanceSheet();
                        else if (activeTab === 'ar-aging') fetchArAging();
                        else fetchApAging();
                      }}
                      disabled={
                        trialBalanceLoading ||
                        balanceSheetLoading ||
                        arAgingLoading ||
                        apAgingLoading
                      }
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-60"
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${trialBalanceLoading ||
                            balanceSheetLoading ||
                            arAgingLoading ||
                            apAgingLoading
                            ? 'animate-spin'
                            : ''
                          }`}
                      />
                      Run Report
                    </button>
                  </>
                )}
              {activeTab === 'income-statement' && (
                <>
                  {plView === 'detail' ? (
                    <button
                      type="button"
                      onClick={handlePlBackToSummary}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back to Income Statement
                    </button>
                  ) : (
                    <>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
                          title="Start date"
                        />
                      </div>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
                          title="End date"
                        />
                      </div>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={includeZeroBalances}
                          onChange={(e) => setIncludeZeroBalances(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        Include zero balances
                      </label>
                      <button
                        type="button"
                        onClick={fetchProfitAndLoss}
                        disabled={profitAndLossLoading}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-60"
                      >
                        <RefreshCw
                          className={`h-4 w-4 ${profitAndLossLoading ? 'animate-spin' : ''}`}
                        />
                        Run Report
                      </button>
                    </>
                  )}
                </>
              )}
              {activeTab === 'journal' && (
                <>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
                      title="Start date"
                    />
                  </div>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
                      title="End date"
                    />
                  </div>
                  <select
                    value={journalAccountFilter}
                    onChange={(e) => setJournalAccountFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white min-w-[180px]"
                  >
                    <option value="">All accounts</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.account_number} — {account.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={journalAutoFilter}
                    onChange={(e) => setJournalAutoFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white min-w-[160px]"
                  >
                    <option value="">All entries</option>
                    <option value="true">System generated</option>
                    <option value="false">Manual</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => fetchJournalEntries(1)}
                    disabled={journalLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-60"
                  >
                    <RefreshCw className={`h-4 w-4 ${journalLoading ? 'animate-spin' : ''}`} />
                    Run Report
                  </button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              {activeTab === 'chart-of-accounts' && canAccessAccounts() && (
                <button
                  type="button"
                  onClick={openCreateModal}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Account
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (activeTab === 'trial-balance') {
                    exportTrialBalance();
                  } else if (activeTab === 'income-statement') {
                    if (plView === 'detail') {
                      void exportProfitAndLossDetail();
                    } else {
                      exportProfitAndLoss();
                    }
                  } else if (activeTab === 'balance-sheet') {
                    exportBalanceSheet();
                  } else if (activeTab === 'ar-aging') {
                    exportArAging();
                  } else if (activeTab === 'ap-aging') {
                    exportApAging();
                  } else if (activeTab === 'journal') {
                    exportJournalEntries();
                  }
                }}
                disabled={
                  (activeTab === 'trial-balance' && !trialBalance) ||
                  (activeTab === 'income-statement' &&
                    (plView === 'detail' ? !selectedPlAccount : !profitAndLoss)) ||
                  (activeTab === 'balance-sheet' && !balanceSheet) ||
                  (activeTab === 'ar-aging' && !arAging) ||
                  (activeTab === 'ap-aging' && !apAging) ||
                  (activeTab === 'journal' && journalEntries.length === 0)
                }
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {activeTab === 'journal' && renderJournal()}

          {activeTab === 'income-statement' && renderIncomeStatement()}

          {activeTab === 'balance-sheet' && renderBalanceSheet()}

          {activeTab === 'trial-balance' && renderTrialBalance()}

          {activeTab === 'ar-aging' && renderArAging()}

          {activeTab === 'ap-aging' && renderApAging()}

          {activeTab === 'chart-of-accounts' && renderChartOfAccounts()}
        </div>
      </main>

      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-800/75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Account</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {renderAccountForm(handleCreate, 'Create Account')}
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 bg-gray-800/75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Account</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAccount(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {renderAccountForm(handleUpdate, 'Save Changes')}
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounts;
